import { defineStore } from 'pinia'
import { AxiosError } from 'axios'
import { EnumerationsServiceType } from 'client'
import { AuthStatus, useAuthStore } from './auth'
import { useServiceDeliverablesStore } from './serviceDeliverables'
import type {
  QueriesChildNavItemDTO,
  QueriesDataVisualizationDTO,
  QueriesGetLeftNavMenuResponse,
} from 'client'
import type {
  QueriesGetLoggedInUserDataResponse,
  QueriesUserServiceDTO,
} from 'client/identity'
import type { Router } from 'vue-router'
import type {
  ILeftNavigationChild,
  ILeftNavigationItem,
  TLeftNavigationTitle,
} from '~/constants/types'
import type { TFeatureFlagKey } from '~/constants/types/featureFlags'
import { useFeatureFlags } from '~/composables/useFeatureFlags'
import { FEATURE_FLAG } from '~/constants/types/featureFlags'
import { OLeftNavigationTitles } from '~/constants/types'
import {
  DATA_VISUALIZATION_CONFIGS,
  DESELECT_SERVICE_LINES_PATHS,
} from '~/constants/navigation'
import { HNRV2 } from '~/helpers'
import { NAV_ICONS } from '~/constants/icons'
import { useUserClaims } from '~/composables/useUserClaims'
import { PATH } from '~/constants/strings'
import { api } from '~/services'
import { getServiceNameForPaths } from '~/helpers/serviceLine'
import { getStoreKey } from '~/helpers/store'

interface ToolsNavItem {
  label: string
  icon: string | ILeftNavigationItem['icon']
  value?: string
  to?: string
  hasServiceLevelPermission: boolean
  featureFlag?: TFeatureFlagKey
  children?: ILeftNavigationChild[]
}
interface NavigationState {
  drawerOpen: boolean
  drawerMini: boolean

  navItemOpen: string
  selectedTopNavigationServiceId: string
  leftNavigationItems: ILeftNavigationItem[]
  leftNavServiceLineId: string

  isLoadingSetNavigationItems: boolean

  isHelpDropdownOpen: boolean
  allToolsNavItems: ToolsNavItem[]
  allGFENavItems: ToolsNavItem[]

  accessibleServiceDeliverableIds: string[]
}

const defaultNavigationState: NavigationState = {
  drawerOpen: true,
  drawerMini: false,

  navItemOpen: '',
  selectedTopNavigationServiceId: '',
  leftNavServiceLineId: '',
  leftNavigationItems: [
    {
      label: OLeftNavigationTitles.DATA_VISUALIZATIONS,
      icon: {
        name: NAV_ICONS.DATA_VISUALIZATIONS,
      },
      children: [],
      value: 'dataVisualizations',
      enumKey: 'dataVisualizationTypeID',
    },
    {
      label: OLeftNavigationTitles.INSIGHTS,
      icon: {
        name: NAV_ICONS.INSIGHTS,
        class: 'relative left-2.5',
      },
      children: [],
    },
    {
      label: OLeftNavigationTitles.REPORTS,
      icon: {
        name: NAV_ICONS.REPORTS,
      },
      children: [],
    },
    {
      label: OLeftNavigationTitles.SERVICE_DELIVERABLES,
      icon: {
        name: NAV_ICONS.SERVICE_DELIVERABLES,
        size: 'sm',
      },
      children: [],
      value: 'serviceDeliverables',
    },
    {
      label: OLeftNavigationTitles.COMMUNICATIONS,
      icon: {
        name: NAV_ICONS.COMMUNICATIONS,
        size: 'sm',
      },
      children: [],
    },
  ],

  allToolsNavItems: [
    {
      label: 'Compliance Checklist',
      icon: NAV_ICONS.COMPLIANCE_CHECKLIST,
      value: 'complianceChecklist',
      to: '/tools/compliance-checklist',
      hasServiceLevelPermission: false,
    },
    {
      label: 'Action-Oriented Programs',
      icon: NAV_ICONS.ACTION_ORIENTED_PROGRAMS,
      value: 'actionOrientedPrograms',
      to: '/tools/action-oriented-programs',
      featureFlag: FEATURE_FLAG.ACTION_ORIENTED_PROGRAMS,
      hasServiceLevelPermission: false,
    },
    {
      label: 'Audit Center',
      icon: NAV_ICONS.AUDIT_CENTER,
      value: 'auditCenter',
      to: '/tools/audit-center',
      featureFlag: FEATURE_FLAG.AUDIT_CENTER,
      hasServiceLevelPermission: false,
    },
  ],

  allGFENavItems: [
    {
      label: 'GFE Tracker',
      icon: NAV_ICONS.GOOD_FAITH_EFFORTS,
      value: 'goodFaithEfforts',
      to: '/gfe/gfe-tracker',
      hasServiceLevelPermission: false,
    },
    {
      label: 'Annual Assessment',
      icon: 'o_assignment',
      value: 'annualAssessment',
      to: '/gfe/annual-assessment',
      hasServiceLevelPermission: false,
    },
  ],

  isLoadingSetNavigationItems: true,
  isHelpDropdownOpen: false,

  accessibleServiceDeliverableIds: [],
}

export const useNavigationStore = defineStore('navigation', {
  state: (): NavigationState => {
    // cannot return reference alone
    // see: https://github.com/vuejs/pinia/issues/484
    return { ...defaultNavigationState }
  },
  getters: {
    isActiveService(): (service: QueriesUserServiceDTO) => boolean {
      return (service) =>
        service.serviceID === this.selectedTopNavigationServiceId
    },
    activeService(): QueriesUserServiceDTO | undefined {
      const AuthStore = useAuthStore()
      const services = AuthStore.user?.services || []

      return services.find(
        (s) => s.serviceID === this.selectedTopNavigationServiceId
      )
    },
    activeServiceName(): string | undefined {
      return this.activeService?.name
    },
    activeServiceNameForPaths(): string | undefined {
      if (!this.activeService) {
        return undefined
      }
      return getServiceNameForPaths(this.activeService.serviceType)
    },
    landingPagePath(): string {
      const AuthStore = useAuthStore()
      return AuthStore.firstPath
    },
    supportAccessPath(): `/${string}` | undefined {
      const {
        hasReadWriteClientSupportAccessPermission,
        hasReadWriteAdminSupportAccessPermission,
      } = useUserClaims()

      return hasReadWriteAdminSupportAccessPermission.value
        ? PATH.ADMIN_SUPPORT_ACCESS
        : hasReadWriteClientSupportAccessPermission.value
        ? PATH.CLIENT_SUPPORT_ACCESS
        : undefined
    },
    noLeftNavigationChildren(): boolean {
      return this.leftNavigationItems.every(
        (leftNavigationItem) => leftNavigationItem?.children?.length === 0
      )
    },
    toolsNavItems(): ToolsNavItem[] {
      const { isFeatureEnabled } = useFeatureFlags()

      return this.allToolsNavItems.filter((toolItem) => {
        const featureFlagCheck = toolItem.featureFlag
          ? isFeatureEnabled(toolItem.featureFlag)
          : true

        return toolItem.hasServiceLevelPermission && featureFlagCheck
      })
    },
    gfeNavItems(): ToolsNavItem[] {
      return this.allGFENavItems.filter((gfeItem) => {
        return gfeItem.hasServiceLevelPermission
      })
    },
    leftNavigationItemsToDisplay(): ILeftNavigationItem[] | ToolsNavItem[] {
      const toolsActive = this.activeService?.name === 'Tools'
      if (toolsActive) {
        return this.toolsNavItems
      }
      const gfeActive = this.activeService?.name === 'Good Faith Efforts'
      if (gfeActive) {
        return this.gfeNavItems
      }

      return this.leftNavigationItems.filter((leftNavigationItem) => {
        return leftNavigationItem.children?.length
      })
    },
  },
  actions: {
    addReportLinksAndRoutes() {
      const reportsParent = this.leftNavigationItems.find(
        (l) => l.label === OLeftNavigationTitles.REPORTS
      )

      if (reportsParent) {
        reportsParent.children = []

        const {
          hasReadWriteReportsServiceFunctionPermission,
          hasReadReportsServiceFunctionPermission,
        } = useUserClaims()

        if (hasReadReportsServiceFunctionPermission.value) {
          // add Reports to Left Nav
          const reportsPath = `/${this.activeServiceNameForPaths}/reports`
          const reportsPageId = `${this.activeServiceNameForPaths}-reports`

          reportsParent.children = [
            {
              id: reportsPageId,
              label: 'Reports',
              to: reportsPath,
            },
          ]

          // Add Report Summaries to left nav
          // And Report Summaries Route (special route case)
          const AuthStore = useAuthStore()
          if (!AuthStore.hideSummaries) {
            const summariesPath = `/${this.activeServiceNameForPaths}/summaries`
            const summariesPageId = `${this.activeServiceNameForPaths}-reports-summaries`

            reportsParent.children.push({
              id: summariesPageId,
              label: 'Summaries',
              to: summariesPath,
            })
          }

          if (
            this.activeService?.serviceType ===
            EnumerationsServiceType.Compliance
          ) {
            // Data Export
            const dataExportPath = `/${this.activeServiceNameForPaths}/reports/data-export`
            const dataExportId = `${this.activeServiceNameForPaths}-reports-data-export`
            reportsParent.children.push({
              id: dataExportId,
              label: 'Data Export',
              to: dataExportPath,
            })

            if (hasReadWriteReportsServiceFunctionPermission.value) {
              const manageReportPath = `/${this.activeServiceNameForPaths}/reports/manage`
              const manageReportId = `${this.activeServiceNameForPaths}-reports-manage`
              reportsParent.children.push({
                id: manageReportId,
                label: `Manage Reports`,
                to: manageReportPath,
              })
            }
          }

          if (
            this.activeService?.serviceType ===
              EnumerationsServiceType.WorkforceAnalytics &&
            AuthStore.isBerkshireRole
          ) {
            // Executive Summary
            const executiveSummaryPath = `/${this.activeServiceNameForPaths}/executive-summary`
            const executiveSummaryId = `${this.activeServiceNameForPaths}-executive-summary`
            reportsParent.children.push({
              id: executiveSummaryId,
              label: 'Executive Summary',
              to: executiveSummaryPath,
            })
          }
        }
      }
    },
    async moveToNewRouteIfOnIt({
      newPath,
      parentTitle,
    }: {
      newPath: string
      parentTitle: TLeftNavigationTitle | undefined
    }) {
      await this.$router.isReady()
      const currentPath = (this.$router as Router).currentRoute.value.path

      if (currentPath === newPath) {
        this.navItemOpen = parentTitle ?? ''
        this.$router.replace(newPath)
      }
    },
    async getLeftNavMenu(
      serviceID: string
    ): Promise<QueriesGetLeftNavMenuResponse | undefined> {
      const response = await HNRV2({
        fn: () =>
          api.serviceLines.apiServiceLinesServiceIDLeftNavMenuGet(serviceID),
      })

      if (response instanceof AxiosError) {
        return
      }

      return response
    },
    async setNavigationItemChildren(serviceID: string): Promise<void> {
      const authStore = useAuthStore()
      const toolsService = authStore.user?.services?.find(
        (service) => service.name.toLowerCase() === 'tools'
      )
      const gfeService = authStore.user?.services?.find(
        (service) => service.name.toLowerCase() === 'good faith efforts'
      )
      const getServiceDeliverablePageRoutePath = (id: string) => {
        return `/${this.activeServiceNameForPaths}/service-deliverables/${id}`
      }

      const { shouldSeeDashboard, shouldSeeDocumentsAndDeliverables } =
        useUserClaims()
      const leftNavigationItemsResult = await this.getLeftNavMenu(serviceID)

      if (!leftNavigationItemsResult) {
        return
      }

      if (serviceID === toolsService?.serviceID) {
        const navItemToToolMap: Record<string, string> = {
          'Compliance Checklist': 'Compliance Checklist',
          Audit: 'Audit Center',
          'Action-Oriented Programs': 'Action-Oriented Programs',
        }

        const enabledTools = new Set(
          leftNavigationItemsResult.tools.map(
            (tool) => navItemToToolMap[tool.name]
          )
        )

        this.allToolsNavItems = this.allToolsNavItems.map((toolNavItem) => {
          return {
            ...toolNavItem,
            hasServiceLevelPermission: enabledTools.has(toolNavItem.label),
          }
        })
      }
      if (serviceID === gfeService?.serviceID) {
        const navItemToGFEMap: Record<string, string> = {
          'Annual Assessment': 'Annual Assessment',
          'GFE Tracker': 'GFE Tracker',
        }

        const enabledGFEPages = new Set(
          leftNavigationItemsResult.goodFaithEfforts.map(
            (gfeItem) => navItemToGFEMap[gfeItem.name]
          )
        )

        this.allGFENavItems = this.allGFENavItems.map((gfeNavItem) => {
          return {
            ...gfeNavItem,
            hasServiceLevelPermission: enabledGFEPages.has(gfeNavItem.label),
          }
        })
      }

      const leftNavItemResultKeys: (keyof QueriesGetLeftNavMenuResponse)[] =
        Object.keys(
          leftNavigationItemsResult
        ) as (keyof QueriesGetLeftNavMenuResponse)[]

      const leftNavItemKeysWithAccess = leftNavItemResultKeys.filter((key) => {
        switch (key) {
          case 'dataVisualizations':
            return shouldSeeDashboard.value
          case 'serviceDeliverablePages':
            return shouldSeeDocumentsAndDeliverables.value
          case 'serviceName':
          default:
            return false
        }
      })

      await Promise.all(
        leftNavItemKeysWithAccess.map(async (leftNavResultKey) => {
          const knownParentLeftNavItem = this.leftNavigationItems.find(
            (leftNavigationItem) => {
              if (
                leftNavResultKey === 'serviceDeliverablePages' &&
                leftNavigationItem.label ===
                  OLeftNavigationTitles.SERVICE_DELIVERABLES
              ) {
                return true
              }

              return leftNavigationItem?.value === leftNavResultKey
            }
          )

          if (knownParentLeftNavItem) {
            const parentTitle = knownParentLeftNavItem.label
            const parentEnumKey = knownParentLeftNavItem.enumKey
            const parentFromResponse =
              leftNavigationItemsResult[leftNavResultKey]
            const hasChildren =
              Array.isArray(parentFromResponse) && parentFromResponse?.length

            if (
              shouldSeeDocumentsAndDeliverables.value &&
              Array.isArray(parentFromResponse) &&
              parentTitle === OLeftNavigationTitles.SERVICE_DELIVERABLES
            ) {
              const {
                hasReadWriteDocumentsDeliverablesServiceFunctionPermission,
              } = useUserClaims()

              if (
                hasReadWriteDocumentsDeliverablesServiceFunctionPermission.value
              ) {
                const manageServiceDeliverablesPath = `/${this.activeServiceNameForPaths}/service-deliverables/manage`
                const manageServiceDeliverablesLeftNavChild: ILeftNavigationChild =
                  {
                    label: 'Manage Deliverables',
                    id: 'manageservicedeliverables',
                    to: manageServiceDeliverablesPath,
                    icon: {
                      name: NAV_ICONS.MANAGE_DELIVERABLES,
                      class: 'mr-2',
                      size: '18px',
                    },
                  }

                const managePageServiceDeliverableID = 'manage'
                this.accessibleServiceDeliverableIds = [
                  ...this.accessibleServiceDeliverableIds,
                  managePageServiceDeliverableID,
                ]

                knownParentLeftNavItem.children.push(
                  manageServiceDeliverablesLeftNavChild
                )

                // add unpublished page ids
                const ServiceDeliverablesStore = useServiceDeliverablesStore()
                const success =
                  await ServiceDeliverablesStore.getServiceDeliverables()

                if (
                  success &&
                  ServiceDeliverablesStore.serviceDeliverablePages.length > 0
                ) {
                  const unpublishedPages =
                    ServiceDeliverablesStore.serviceDeliverablePages.filter(
                      (page) => {
                        return !page.active
                      }
                    )
                  unpublishedPages.forEach(
                    (upPage) =>
                      (this.accessibleServiceDeliverableIds = [
                        ...this.accessibleServiceDeliverableIds,
                        upPage.pageID,
                      ])
                  )
                }
              }

              // add ServiceDeliverablePage entries
              parentFromResponse.forEach((childFromResponse) => {
                const newRoutePath = getServiceDeliverablePageRoutePath(
                  childFromResponse.id
                )
                this.accessibleServiceDeliverableIds = [
                  ...this.accessibleServiceDeliverableIds,
                  childFromResponse.id,
                ]
                const newLeftNavChild: ILeftNavigationChild = {
                  label: childFromResponse.name,
                  id: childFromResponse.id,
                  to: newRoutePath,
                }

                knownParentLeftNavItem.children.push(newLeftNavChild)
              })

              return
            }

            // Everything other than service deliverables
            if (hasChildren && parentEnumKey) {
              knownParentLeftNavItem.children = parentFromResponse.reduce(
                (
                  acc: any[],
                  childFromResponse:
                    | QueriesDataVisualizationDTO
                    | QueriesChildNavItemDTO
                ) => {
                  const isDataVizLeftNav = Object.hasOwn(
                    childFromResponse,
                    'dataVisualizationTypeID'
                  )

                  if (
                    !childFromResponse.name ||
                    !childFromResponse.id ||
                    !isDataVizLeftNav
                  ) {
                    return acc
                  }

                  const getDataVisualizationPageRoutePath = (
                    routeName: string
                  ) => {
                    return `/${this.activeServiceNameForPaths}/data-viz/${routeName}`
                  }

                  const dataVizRouteName = childFromResponse.name
                    .toLowerCase()
                    .replaceAll(' ', '-')

                  const dataVizPath =
                    getDataVisualizationPageRoutePath(dataVizRouteName)

                  const iconName =
                    DATA_VISUALIZATION_CONFIGS[dataVizRouteName]?.iconName ??
                    undefined

                  const dataVizIcon = iconName
                    ? {
                        name: iconName,
                        class: 'mr-2',
                        size: '18px',
                      }
                    : undefined

                  const childForLeftNav = {
                    label: childFromResponse.name,
                    id: childFromResponse.id,
                    to: dataVizPath,
                    ...(dataVizIcon && {
                      icon: dataVizIcon,
                    }),
                  }
                  acc.push(childForLeftNav)

                  return acc
                },
                []
              )
            } else {
              knownParentLeftNavItem.children = []
            }
          }
        })
      )
    },
    async setNavigationItems(
      {
        serviceID,
        forceUpdate,
      }: { serviceID: string; forceUpdate?: boolean } = {
        serviceID: '',
        forceUpdate: false,
      }
    ) {
      this.isLoadingSetNavigationItems = true

      try {
        const isServiceLineDifferent = serviceID !== this.leftNavServiceLineId
        const shouldUpdate =
          isServiceLineDifferent || this.noLeftNavigationChildren || forceUpdate
        if (shouldUpdate) {
          // reset children
          this.leftNavigationItems = this.leftNavigationItems.map((lf) => ({
            ...lf,
            children: [],
          }))

          if (this.activeServiceNameForPaths) {
            await this.setNavigationItemChildren(serviceID)
          }

          this.leftNavServiceLineId = serviceID

          const { shouldSeeReports } = useUserClaims()

          if (shouldSeeReports.value) {
            // TODO: Remove below conditional when other service lines support reports page
            // eslint-disable-next-line unicorn/no-lonely-if
            const serviceLineSupportsReports =
              this.activeService?.serviceType ===
                EnumerationsServiceType.Compliance ||
              this.activeService?.serviceType ===
                EnumerationsServiceType.WorkforceAnalytics
            if (serviceLineSupportsReports) {
              await this.addReportLinksAndRoutes()
            }
          }
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.isLoadingSetNavigationItems = false
      }
    },
    async setActiveService(serviceID: string) {
      if (serviceID === this.selectedTopNavigationServiceId) return
      this.navItemOpen = ''
      this.selectedTopNavigationServiceId = serviceID

      await this.setNavigationItems({ serviceID })
    },
    async setServicesIfPathIsOnKnownService(path?: string): Promise<boolean> {
      const AuthStore = useAuthStore()
      const userServices = AuthStore.user?.services

      const serviceLineFromPath = path
        ? this.serviceLineNameFromTargetPath(path)
        : this.serviceLineNameFromCurrentPath()

      // set the active service based on the path
      const knownService = userServices?.find((userService) => {
        if (serviceLineFromPath?.toLowerCase() === 'wfa') {
          return (
            userService?.serviceType ===
            EnumerationsServiceType.WorkforceAnalytics
          )
        }
        if (serviceLineFromPath?.toLowerCase() === 'gfe') {
          return (
            userService?.serviceType ===
            EnumerationsServiceType.GoodFaithEfforts
          )
        }

        return (
          serviceLineFromPath?.toLowerCase() ===
          userService?.name?.toLowerCase()
        )
      })

      if (knownService) {
        this.selectedTopNavigationServiceId = knownService.serviceID
        await this.setNavigationItems({
          serviceID: this.selectedTopNavigationServiceId,
        })
        return true
      }
      return false
    },
    async initializeNavigation(firstPath?: string) {
      const currentPath = firstPath || window.location.pathname

      // don't setup navigation if on a path that doesn't require it
      if (
        DESELECT_SERVICE_LINES_PATHS.includes(currentPath) ||
        (this.$router as Router).currentRoute.value.path.includes(
          'report-preview'
        )
      ) {
        return
      }

      const AuthStore = useAuthStore()
      const userServices = AuthStore.user?.services

      const knownService = await this.setServicesIfPathIsOnKnownService(
        currentPath
      )

      if (knownService) {
        return
      }

      if (userServices?.length) {
        // check if this is an old service name
        // if not, allow path and go to 404 (should work out of the box)

        const service = this.findServiceLine(currentPath)

        // ! at this point, you know you should have a service line
        // ! and the one in the path is not recognized
        // ! so you can route to 404 here

        if (!service) {
          this.isLoadingSetNavigationItems = false
          this.$router.replace({
            name: 'NotFound',
            params: {
              pathMatch: currentPath.substring(1).split('/'),
            },
          })
          return
        }

        this.selectedTopNavigationServiceId = service.serviceID

        await (this.$router as Router).isReady()
        // replace old serviceline in URL with updated one
        if (
          'serviceLine' in this.$router.currentRoute.value.params &&
          this.$router.currentRoute.value.params.serviceLine
        ) {
          ;(this.$router as Router).replace({
            name: this.$router.currentRoute.value.name,
            params: {
              ...this.$router.currentRoute.value.params,
              serviceLine: service.name.toLowerCase(),
            },
            query: this.$router.currentRoute.value.query,
          })
        }

        // get left nav based on that service
        await this.setNavigationItems({ serviceID: service.serviceID })
      }

      if (AuthStore.authStatus === AuthStatus.InitiatingUserSession) {
        // only set drawer to be open if you just logged in
        this.setDrawerToDefaultStateAfterLogin()
      }
    },
    setDrawerToDefaultStateAfterLogin() {
      const AuthStore = useAuthStore()
      // set to start as open
      this.drawerOpen = true

      AuthStore.justLoggedIn = false
    },
    serviceLineNameFromTargetPath(path: string): string | undefined {
      const serviceInPath = path?.split('/')?.[1] ?? ''
      return serviceInPath
    },
    serviceLineNameFromCurrentPath(): string | undefined {
      if ('serviceLine' in this.$router.currentRoute.value.params) {
        return this.$router.currentRoute.value.params.serviceLine
      }

      const pathToCheck = (this.$router as Router).currentRoute.value.path
      const serviceInPath = pathToCheck?.split('/')?.[1] ?? ''

      // WFA service line is a special case, not expressed as a :serviceLine parameter
      if (serviceInPath === 'wfa') {
        return 'wfa'
      }

      return undefined
    },
    findServiceLine(
      targetPath?: string
    ): QueriesGetLoggedInUserDataResponse['services'][number] | undefined {
      const AuthStore = useAuthStore()

      const serviceLineFromPath =
        (targetPath
          ? this.serviceLineNameFromTargetPath(targetPath)
          : undefined) ?? this.serviceLineNameFromCurrentPath()

      if (!serviceLineFromPath) {
        return undefined
      }

      switch (serviceLineFromPath) {
        case 'eeo':
        case 'ndp':
        case 'aap':
        case 'compliance':
          return AuthStore.user?.services.find((service) => {
            return service.serviceType === EnumerationsServiceType.Compliance
          })
        case 'sra':
        case 'wfa':
          return AuthStore.user?.services.find((service) => {
            return (
              service.serviceType === EnumerationsServiceType.WorkforceAnalytics
            )
          })
        case 'gfe':
          return AuthStore.user?.services.find((service) => {
            return (
              service.serviceType === EnumerationsServiceType.GoodFaithEfforts
            )
          })
      }

      return (
        AuthStore.user?.services.find((service) => {
          return (
            service.name.toLowerCase() === serviceLineFromPath?.toLowerCase()
          )
        }) ?? undefined
      )
    },
  },
  persist: {
    pick: ['drawerOpen', 'drawerMini'],
    key: getStoreKey('navigation'),
  },
})
